import type {ReactNode} from "react";
import {ArrowPathIcon} from "@heroicons/react/24/outline";
import Dropdown from "../../../general/Dropdown.tsx";
import {SecondaryButton} from "../../../general/SecondaryButton.tsx";
import {fieldLabelClasses} from "../../../general/fieldStyles.ts";
import {AccountSearchInput} from "./AccountSearchInput.tsx";
import {CurrencyField} from "./CurrencyField.tsx";
import {ExpandCollapseButton} from "./ExpandCollapseButton.tsx";
import {PeriodDateRangeField} from "./PeriodDateRangeField.tsx";
import {PredictedAccountsToggle} from "./PredictedAccountsToggle.tsx";
import type { CurrencyEnum } from '../../../../generated/enums.ts';

interface YtdOption {
    value: string;
    label: string;
}

interface PeriodicToolbarProps {
    dateFrom: string;
    dateTo: string;
    onDateRangeChange: (from: string, to: string) => void;
    leading?: ReactNode;
    ytd?: {
        monthIndex: number;
        options: YtdOption[];
        onChange: (index: number) => void;
    };
    selectedCurrency: CurrencyEnum;
    currencyOptions: CurrencyEnum[];
    onCurrencyChange: (currency: CurrencyEnum) => void;
    showPredictedAccounts: boolean;
    onPredictedChange: (value: boolean) => void;
    searchQuery: string;
    onSearchChange: (value: string) => void;
    onRefresh?: () => Promise<void>;
    isRefreshingCache: boolean;
    isLoading: boolean;
    beyondDefault: boolean;
    onToggleAll: () => void;
}

export function PeriodicToolbar({
    dateFrom,
    dateTo,
    onDateRangeChange,
    leading,
    ytd,
    selectedCurrency,
    currencyOptions,
    onCurrencyChange,
    showPredictedAccounts,
    onPredictedChange,
    searchQuery,
    onSearchChange,
    onRefresh,
    isRefreshingCache,
    isLoading,
    beyondDefault,
    onToggleAll,
}: PeriodicToolbarProps) {
    return (
        <div className="mb-6 space-y-3.5">
            <div className="flex flex-wrap items-end gap-3">
                {leading}

                <PeriodDateRangeField
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onChange={onDateRangeChange}
                />

                {ytd && (
                    <div className="flex flex-col gap-1.5">
                        <span className={fieldLabelClasses}>YTD t/m</span>
                        <div className="w-[190px]">
                            <Dropdown
                                selected={{
                                    value: String(ytd.monthIndex),
                                    label: ytd.options[ytd.monthIndex]?.label ?? "—",
                                }}
                                options={ytd.options}
                                onUpdateFunc={(value) => ytd.onChange(Number(value))}
                            />
                        </div>
                    </div>
                )}

                <CurrencyField
                    selectedCurrency={selectedCurrency}
                    options={currencyOptions}
                    onChange={onCurrencyChange}
                />

                <PredictedAccountsToggle
                    checked={showPredictedAccounts}
                    onChange={onPredictedChange}
                />
            </div>

            <div className="flex flex-wrap items-end justify-between gap-3">
                <AccountSearchInput value={searchQuery} onChange={onSearchChange} />

                <div className="flex flex-wrap items-center gap-2">
                    {onRefresh && (
                        <SecondaryButton
                            title="Vernieuwen"
                            icon={ArrowPathIcon}
                            onClick={() => void onRefresh()}
                            disabled={isRefreshingCache || isLoading || !dateFrom || !dateTo}
                            loading={isRefreshingCache}
                            tooltip="Berekent de cijfers opnieuw voor deze klant. Gebruik dit als de cijfers mogelijk verouderd zijn."
                        />
                    )}
                    <ExpandCollapseButton
                        beyondDefault={beyondDefault}
                        disabled={!!searchQuery.trim()}
                        onClick={onToggleAll}
                    />
                </div>
            </div>
        </div>
    );
}
